import { useCallback, useEffect, useRef, useState } from 'react';
import { Outlet, createFileRoute, useRouter } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { Loader2, ShieldOff } from 'lucide-react';
import { AppShell } from '../components/layout/AppShell.jsx';
import { RecordDrawers } from '../components/layout/RecordDrawers.jsx';
import { PageMetaProvider } from '../components/layout/pageMeta.jsx';
import { SignInPage } from '../features/auth/SignInPage.jsx';
import { useAccess } from '../lib/access.js';
import { clearSessionMarker, loadSession, readSessionMarker, signOut, writeSessionMarker } from '../lib/session.js';
import { Icon } from '../ui/Icon.jsx';
import { Button } from '../ui/Button.jsx';
import { EmptyState, ErrorState } from '../ui/States.jsx';
import { BRAND } from '../lib/brand.js';

/**
 * Every signed-in screen. Auth is asynchronous and the shell does not block on it
 * (tkt-back-office routes/__root.tsx): a browser that was signed in last time paints the shell
 * at once from a local marker and confirms the session in the background. Only a browser that has
 * never signed in here waits on the check.
 *
 * `unknown` (the backend could not be asked) changes nothing: a dropped connection never signs
 * anyone out.
 */
export const Route = createFileRoute('/_app')({ component: AppLayout });

function AppLayout() {
  const router = useRouter();
  const queryClient = useQueryClient();
  const marker = readSessionMarker();
  const [state, setState] = useState(marker ? 'authed' : 'checking');
  const [user, setUser] = useState(marker ? { email: marker.email, name: marker.name } : null);
  const stateRef = useRef(state);
  useEffect(() => { stateRef.current = state; }, [state]);

  const refresh = useCallback(async () => {
    const justSignedIn = stateRef.current === 'unauthed';
    const s = await loadSession();
    if (s.status === 'unknown') {
      if (stateRef.current === 'checking') setState('unreachable');
      return;
    }
    if (s.status === 'authed') {
      setUser(s.user);
      writeSessionMarker(s.user);
      setState('authed');
      if (justSignedIn) { queryClient.clear(); router.invalidate(); }
    } else {
      clearSessionMarker();
      setUser(null);
      setState('unauthed');
    }
  }, [queryClient, router]);

  useEffect(() => { refresh(); }, [refresh]);

  const onSignOut = async () => {
    await signOut();
    queryClient.clear();
    setUser(null);
    setState('unauthed');
  };

  if (state === 'checking') {
    return (
      <div className="flex min-h-screen items-center justify-center gap-2 text-base text-ink-subtle">
        <Icon icon={Loader2} size={16} className="animate-spin" />
        Checking your session…
      </div>
    );
  }
  if (state === 'unreachable') {
    return (
      <div className="flex min-h-screen items-center justify-center p-4">
        <ErrorState error={{ kind: 'network', message: `The ${BRAND.product} server did not answer. Your session, if any, is untouched.` }} onRetry={refresh} />
      </div>
    );
  }
  if (state === 'unauthed') return <SignInPage onSignedIn={refresh} />;

  return (
    <PageMetaProvider>
      <StaffShell user={user} onSignOut={onSignOut} />
    </PageMetaProvider>
  );
}

/** Platform staff only. Anyone else signed in gets a clear "no access", not an empty console. */
function StaffShell({ user, onSignOut }) {
  const access = useAccess();
  const roleLabel = access.isSuperAdmin ? 'Super admin' : access.me?.isPlatformStaff ? 'Platform staff' : access.me?.orgRole || '';

  if (!access.isLoading && !access.error && !access.isStaff) {
    return (
      <div className="flex min-h-screen items-center justify-center p-4">
        <EmptyState
          icon={ShieldOff}
          title={`This console is for ${BRAND.product} platform staff`}
          body={`You're signed in as ${user?.email || 'someone'} who isn't platform staff. ${BRAND.seller.many.replace(/^\w/, (c) => c.toUpperCase())} manage their events in their own dashboard.`}
          action={<Button onClick={onSignOut}>Sign out</Button>}
        />
      </div>
    );
  }

  return (
    <AppShell user={user} roleLabel={roleLabel} onSignOut={onSignOut}>
      {access.error && access.error.kind !== 'unauthorized' ? (
        <ErrorState error={access.error} title="Couldn’t confirm your access" onRetry={() => window.location.reload()} />
      ) : (
        <>
          <Outlet />
          <RecordDrawers />
        </>
      )}
    </AppShell>
  );
}
